import React, { useState, useMemo } from 'react';
import { meetingService, CreateMeetingParams } from '../services/meetingService';
import styles from '../styles/createMeetingModal.module.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBREV: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

const DURATION_OPTIONS = [
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '90 minutes' },
  { value: '120', label: '2 hours' },
  { value: 'other', label: 'Other' },
];

const FORMAT_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'telemeeting', label: 'Telemeeting' },
];

const LINK_VISIBILITY_OPTIONS = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'members_only', label: 'Members only' },
  { value: 'dont_show', label: "Don't show – contact for location" },
];

// Follow-the-sun order: Pacific first, then eastward around the world
const TIMEZONES = [
  { value: 'Pacific/Midway', label: 'Midway (UTC-11)', abbrev: 'SST' },
  { value: 'Pacific/Honolulu', label: 'Hawaii', abbrev: 'HST' },
  { value: 'America/Anchorage', label: 'Alaska', abbrev: 'AKT' },
  { value: 'America/Los_Angeles', label: 'Pacific (US & Canada)', abbrev: 'PT' },
  { value: 'America/Tijuana', label: 'Tijuana', abbrev: 'PT' },
  { value: 'America/Denver', label: 'Mountain (US & Canada)', abbrev: 'MT' },
  { value: 'America/Phoenix', label: 'Arizona', abbrev: 'MST' },
  { value: 'America/Chihuahua', label: 'Chihuahua', abbrev: 'MT' },
  { value: 'America/Chicago', label: 'Central (US & Canada)', abbrev: 'CT' },
  { value: 'America/Mexico_City', label: 'Mexico City', abbrev: 'CT' },
  { value: 'America/Guatemala', label: 'Central America', abbrev: 'CST' },
  { value: 'America/New_York', label: 'Eastern (US & Canada)', abbrev: 'ET' },
  { value: 'America/Bogota', label: 'Bogotá', abbrev: 'COT' },
  { value: 'America/Lima', label: 'Lima', abbrev: 'PET' },
  { value: 'America/Caracas', label: 'Caracas', abbrev: 'VET' },
  { value: 'America/Santiago', label: 'Santiago', abbrev: 'CLT' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires', abbrev: 'ART' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', abbrev: 'BRT' },
  { value: 'America/St_Johns', label: 'Newfoundland', abbrev: 'NST' },
  { value: 'America/Halifax', label: 'Atlantic (Canada)', abbrev: 'AST' },
  { value: 'America/Godthab', label: 'Greenland', abbrev: 'WGT' },
  { value: 'Atlantic/Azores', label: 'Azores', abbrev: 'AZOT' },
  { value: 'Europe/London', label: 'London', abbrev: 'GMT' },
  { value: 'Europe/Dublin', label: 'Dublin', abbrev: 'GMT' },
  { value: 'Europe/Paris', label: 'Paris', abbrev: 'CET' },
  { value: 'Europe/Berlin', label: 'Berlin', abbrev: 'CET' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', abbrev: 'CET' },
  { value: 'Europe/Brussels', label: 'Brussels', abbrev: 'CET' },
  { value: 'Europe/Madrid', label: 'Madrid', abbrev: 'CET' },
  { value: 'Europe/Rome', label: 'Rome', abbrev: 'CET' },
  { value: 'Europe/Stockholm', label: 'Stockholm', abbrev: 'CET' },
  { value: 'Europe/Athens', label: 'Athens', abbrev: 'EET' },
  { value: 'Europe/Helsinki', label: 'Helsinki', abbrev: 'EET' },
  { value: 'Europe/Istanbul', label: 'Istanbul', abbrev: 'TRT' },
  { value: 'Europe/Moscow', label: 'Moscow', abbrev: 'MSK' },
  { value: 'Africa/Cairo', label: 'Cairo', abbrev: 'EET' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', abbrev: 'SAST' },
  { value: 'Africa/Lagos', label: 'Lagos', abbrev: 'WAT' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem', abbrev: 'IST' },
  { value: 'Asia/Dubai', label: 'Dubai', abbrev: 'GST' },
  { value: 'Asia/Karachi', label: 'Karachi', abbrev: 'PKT' },
  { value: 'Asia/Kolkata', label: 'India', abbrev: 'IST' },
  { value: 'Asia/Dhaka', label: 'Dhaka', abbrev: 'BST' },
  { value: 'Asia/Bangkok', label: 'Bangkok', abbrev: 'ICT' },
  { value: 'Asia/Singapore', label: 'Singapore', abbrev: 'SGT' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', abbrev: 'HKT' },
  { value: 'Asia/Shanghai', label: 'Shanghai', abbrev: 'CST' },
  { value: 'Asia/Tokyo', label: 'Tokyo', abbrev: 'JST' },
  { value: 'Asia/Seoul', label: 'Seoul', abbrev: 'KST' },
  { value: 'Australia/Sydney', label: 'Sydney', abbrev: 'AEST' },
  { value: 'Australia/Melbourne', label: 'Melbourne', abbrev: 'AEST' },
  { value: 'Australia/Perth', label: 'Perth', abbrev: 'AWST' },
  { value: 'Pacific/Auckland', label: 'Auckland', abbrev: 'NZST' },
  { value: 'Pacific/Fiji', label: 'Fiji', abbrev: 'FJT' },
];

function formatTimeForName(startTime: string): string {
  if (!startTime || !/^\d{1,2}:\d{2}$/.test(startTime)) return '';
  const [h, m] = startTime.split(':').map(Number);
  if (h === 0 && m === 0) return 'Midnight';
  if (h === 12 && m === 0) return 'Noon';
  if (h === 0) return `12:${m.toString().padStart(2, '0')}am`;
  if (h < 12) return `${h}:${m.toString().padStart(2, '0')}am`;
  if (h === 12) return `12:${m.toString().padStart(2, '0')}pm`;
  return `${h - 12}:${m.toString().padStart(2, '0')}pm`;
}

function getDayFromDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T12:00:00');
  const day = d.getDay();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[day];
}

/** Add deltaMinutes to time string "HH:mm" (24h). Wraps at midnight. */
function addMinutesToTime(timeStr: string, deltaMinutes: number): string {
  if (!timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return timeStr || '00:00';
  const [h, m] = timeStr.split(':').map(Number);
  let total = h * 60 + m + deltaMinutes;
  total = ((total % 1440) + 1440) % 1440;
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${nh.toString().padStart(2, '0')}:${nm.toString().padStart(2, '0')}`;
}

interface CreateMeetingModalProps {
  onClose: () => void;
  onSuccess: (meetingCode: string, shouldNavigate?: boolean) => void;
}

const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState<'meeting' | 'workshop'>('meeting');
  const [format, setFormat] = useState('online');
  const [location, setLocation] = useState('');
  const [scheduleDay, setScheduleDay] = useState('');
  const [scheduleStartTime, setScheduleStartTime] = useState('05:00');
  const [scheduleDurationMinutes, setScheduleDurationMinutes] = useState<string>('60');
  const [scheduleDurationCustom, setScheduleDurationCustom] = useState('');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [linkVisibility, setLinkVisibility] = useState('everyone');
  const [meetingName, setMeetingName] = useState('');
  const [telemeetingDialIn, setTelemeetingDialIn] = useState('');
  const [telemeetingPasscode, setTelemeetingPasscode] = useState('');
  const [description, setDescription] = useState('');
  const [workshopFirstSessionDate, setWorkshopFirstSessionDate] = useState('');
  const [workshopDurationMinutes, setWorkshopDurationMinutes] = useState<string>('90');
  const [workshopDurationCustom, setWorkshopDurationCustom] = useState('');
  const [createdMeetingCode, setCreatedMeetingCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const programLabel = 'SAA';
  const formatSuffix = format === 'in_person' ? 'In-person' : format === 'hybrid' ? 'Hybrid' : 'Online';
  const tzOption = TIMEZONES.find((t) => t.value === timezone);
  const tzAbbrev = tzOption?.abbrev || '';

  const handleNext = () => {
    setError(null);
    if (step === 1) {
      setFieldErrors({});
      setStep(2);
    } else if (step === 2) {
      const errs: Record<string, string> = {};
      if (!meetingName.trim()) errs.meetingName = 'Meeting name is required';
      if (kind === 'meeting') {
        if (!scheduleDay) errs.scheduleDay = 'Please select a day of the week';
        if (!scheduleStartTime || !/^\d{1,2}:\d{2}$/.test(scheduleStartTime)) errs.scheduleStartTime = 'Please select a start time';
        if (scheduleDurationMinutes === 'other' && !scheduleDurationCustom.trim()) errs.scheduleDurationCustom = 'Please specify the duration (e.g. 3 hours)';
      }
      if (kind === 'workshop') {
        if (!workshopFirstSessionDate) errs.workshopFirstSessionDate = 'Please enter the first session date';
        if (workshopDurationMinutes === 'other' && !workshopDurationCustom.trim()) errs.workshopDurationCustom = 'Please specify the session duration (e.g. 3 hours)';
      }
      if ((format === 'hybrid' || format === 'in_person') && !location.trim()) errs.location = 'Please enter a location for Hybrid or In-Person';
      if (format === 'telemeeting' && !telemeetingDialIn.trim()) errs.telemeetingDialIn = 'Please enter the dial-in number for Telemeeting';
      setFieldErrors(errs);
      if (Object.keys(errs).length > 0) return;
      setFieldErrors({});
      setStep(3);
    } else if (step === 3) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let scheduleDayParam: string | undefined =
        kind === 'meeting' ? scheduleDay || undefined : undefined;
      if (kind === 'workshop' && workshopFirstSessionDate && /^\d{4}-\d{2}-\d{2}$/.test(workshopFirstSessionDate)) {
        try {
          const d = new Date(workshopFirstSessionDate + 'T12:00:00');
          scheduleDayParam = d.toLocaleDateString('en-US', { weekday: 'long' }) || undefined;
        } catch {
          /* leave undefined */
        }
      }
      const params: CreateMeetingParams = {
        name: meetingName.trim(),
        program_id: 'saa',
        kind,
        description: description.trim() || undefined,
        format,
        location: location.trim() || undefined,
        schedule_day: scheduleDayParam,
        schedule_start_time: scheduleStartTime || undefined,
        schedule_duration_minutes: scheduleDurationMinutes !== '' && scheduleDurationMinutes !== 'other' ? Number(scheduleDurationMinutes) : undefined,
        schedule_duration_custom: scheduleDurationMinutes === 'other' ? scheduleDurationCustom.trim() || undefined : undefined,
        timezone: timezone || undefined,
        link_visibility: linkVisibility,
      };
      if (format === 'telemeeting') {
        params.telemeeting_dial_in = telemeetingDialIn.trim() || undefined;
        params.telemeeting_passcode = telemeetingPasscode.trim() || undefined;
      }
      if (kind === 'workshop') {
        params.workshop_first_session_date = workshopFirstSessionDate || undefined;
        params.workshop_duration_minutes = workshopDurationMinutes !== '' && workshopDurationMinutes !== 'other' ? Number(workshopDurationMinutes) : undefined;
        params.workshop_duration_custom = workshopDurationMinutes === 'other' ? workshopDurationCustom.trim() || undefined : undefined;
      }
      const result = await meetingService.createMeeting(params);
      setCreatedMeetingCode(result.meeting_code);
      setStep(4);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    if (step === 4) onSuccess(createdMeetingCode, false);
    else onClose();
  };

  const formatLabel = (val: string) => FORMAT_OPTIONS.find((o) => o.value === val)?.label || val;
  const linkVisibilityLabel = (val: string) => LINK_VISIBILITY_OPTIONS.find((o) => o.value === val)?.label || val;
  const durationLabel = (val: string, custom?: string) =>
    val === 'other' ? (custom || 'Other') : (DURATION_OPTIONS.find((o) => o.value === val)?.label || `${val} min`);

  return (
    <div className={styles.overlay} onClick={handleDismiss}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>
            {step === 4
              ? `${kind === 'workshop' ? 'Workshop' : 'Meeting'} home page created`
              : step === 1
                ? `Create ${kind === 'workshop' ? 'Workshop' : 'Meeting'} home page`
                : `Create ${kind === 'workshop' ? 'Workshop' : 'Meeting'} home page`}
          </h2>
          <button type="button" className={styles.closeBtn} onClick={handleDismiss} aria-label="Close">
            ×
          </button>
        </div>
        <div className={styles.body}>
          {step === 1 && (
            <>
              <label className={styles.label}>What would you like to create?</label>
              <div className={styles.typeRow}>
                <label className={styles.radioWrap}>
                  <input
                    type="radio"
                    name="kind"
                    checked={kind === 'meeting'}
                    onChange={() => setKind('meeting')}
                    className={styles.radio}
                  />
                  <span className={styles.radioLabel}>Meeting</span>
                </label>
                <label className={styles.radioWrap}>
                  <input
                    type="radio"
                    name="kind"
                    checked={kind === 'workshop'}
                    onChange={() => setKind('workshop')}
                    className={styles.radio}
                  />
                  <span className={styles.radioLabel}>Workshop</span>
                </label>
              </div>
              <div className={styles.typeExplanation}>
                <p><strong>Meeting</strong> – A recurring gathering (e.g. weekly) where recovery addicts share, read, and support each other. Same day and time each week.</p>
                <p><strong>Workshop</strong> – A multi-session series (e.g. Work through the 12 steps together) with a start and end date. May have milestones and a defined structure.</p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <label className={styles.label}>Meeting format</label>
              <div className={styles.formatRow}>
                {FORMAT_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`${styles.formatBtn} ${format === o.value ? styles.formatBtnActive : ''}`}
                    onClick={() => setFormat(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              {(format === 'hybrid' || format === 'in_person') && (
                <>
                  <label className={styles.label}>
                    Location
                    {format === 'in_person' && ' (Address)'}
                    {format === 'hybrid' && ' (Address and/or online details)'}
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. 123 Main St, City"
                    className={styles.input}
                  />
                  {fieldErrors.location && <span className={styles.fieldError}>{fieldErrors.location}</span>}
                </>
              )}

              {format === 'telemeeting' && (
                <>
                  <label className={styles.label}>Dial-in number</label>
                  <input
                    type="text"
                    value={telemeetingDialIn}
                    onChange={(e) => setTelemeetingDialIn(e.target.value)}
                    placeholder="e.g. +1 (555) 123-4567"
                    className={styles.input}
                  />
                  {fieldErrors.telemeetingDialIn && <span className={styles.fieldError}>{fieldErrors.telemeetingDialIn}</span>}
                  <label className={styles.label}>Passcode (if required)</label>
                  <input
                    type="text"
                    value={telemeetingPasscode}
                    onChange={(e) => setTelemeetingPasscode(e.target.value)}
                    placeholder="Optional"
                    className={styles.input}
                  />
                </>
              )}

              {kind === 'meeting' && (
                <>
                  <label className={styles.label}>Recurring schedule</label>
                  <div className={styles.row2}>
                    <select
                      value={scheduleDay}
                      onChange={(e) => setScheduleDay(e.target.value)}
                      className={`${styles.select} ${styles.daySelect}`}
                    >
                      <option value="">Day</option>
                      {DAYS_OF_WEEK.map((d) => (
                        <option key={d} value={d}>{DAY_ABBREV[d]}</option>
                      ))}
                    </select>
                    <div className={styles.timeWithStepper}>
                      <input
                        type="time"
                        value={scheduleStartTime}
                        onChange={(e) => setScheduleStartTime(e.target.value)}
                        step="1800"
                        className={styles.input}
                      />
                      <div className={styles.timeStepper}>
                        <button type="button" className={styles.timeStepBtn} onClick={() => setScheduleStartTime((t) => addMinutesToTime(t, 30))} title="Add 30 minutes" aria-label="Add 30 minutes">▲</button>
                        <button type="button" className={styles.timeStepBtn} onClick={() => setScheduleStartTime((t) => addMinutesToTime(t, -30))} title="Subtract 30 minutes" aria-label="Subtract 30 minutes">▼</button>
                      </div>
                    </div>
                    <select
                      value={scheduleDurationMinutes}
                      onChange={(e) => setScheduleDurationMinutes(e.target.value)}
                      className={`${styles.select} ${styles.durationSelect}`}
                    >
                      {DURATION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  {fieldErrors.scheduleDay && <span className={styles.fieldError}>{fieldErrors.scheduleDay}</span>}
                  {fieldErrors.scheduleStartTime && <span className={styles.fieldError}>{fieldErrors.scheduleStartTime}</span>}
                  {scheduleDurationMinutes === 'other' && (
                    <>
                      <label className={styles.label}>Duration (e.g. 3 hours)</label>
                      <input
                        type="text"
                        value={scheduleDurationCustom}
                        onChange={(e) => setScheduleDurationCustom(e.target.value)}
                        placeholder="e.g. 3 hours"
                        className={styles.input}
                      />
                      {fieldErrors.scheduleDurationCustom && <span className={styles.fieldError}>{fieldErrors.scheduleDurationCustom}</span>}
                    </>
                  )}
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className={styles.select}
                  >
                    {TIMEZONES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </>
              )}

              {kind === 'workshop' && (
                <>
                  <label className={styles.label}>First session</label>
                  <div className={styles.row2}>
                    <input
                      type="date"
                      value={workshopFirstSessionDate}
                      onChange={(e) => setWorkshopFirstSessionDate(e.target.value)}
                      className={styles.input}
                    />
                    <div className={styles.timeWithStepper}>
                      <input
                        type="time"
                        value={scheduleStartTime}
                        onChange={(e) => setScheduleStartTime(e.target.value)}
                        className={styles.input}
                      />
                      <div className={styles.timeStepper}>
                        <button type="button" className={styles.timeStepBtn} onClick={() => setScheduleStartTime((t) => addMinutesToTime(t, 30))} title="Add 30 minutes" aria-label="Add 30 minutes">▲</button>
                        <button type="button" className={styles.timeStepBtn} onClick={() => setScheduleStartTime((t) => addMinutesToTime(t, -30))} title="Subtract 30 minutes" aria-label="Subtract 30 minutes">▼</button>
                      </div>
                    </div>
                  </div>
                  {fieldErrors.workshopFirstSessionDate && <span className={styles.fieldError}>{fieldErrors.workshopFirstSessionDate}</span>}
                  {fieldErrors.scheduleStartTime && <span className={styles.fieldError}>{fieldErrors.scheduleStartTime}</span>}
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className={styles.select}
                  >
                    {TIMEZONES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <label className={styles.label}>Session duration</label>
                  <select
                    value={workshopDurationMinutes}
                    onChange={(e) => setWorkshopDurationMinutes(e.target.value)}
                    className={styles.select}
                  >
                    {DURATION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {workshopDurationMinutes === 'other' && (
                    <>
                      <label className={styles.label}>Duration (e.g. 3 hours)</label>
                      <input
                        type="text"
                        value={workshopDurationCustom}
                        onChange={(e) => setWorkshopDurationCustom(e.target.value)}
                        placeholder="e.g. 3 hours"
                        className={styles.input}
                      />
                      {fieldErrors.workshopDurationCustom && <span className={styles.fieldError}>{fieldErrors.workshopDurationCustom}</span>}
                    </>
                  )}
                </>
              )}

              <label className={styles.label}>Meeting name</label>
              <input
                type="text"
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)}
                placeholder={kind === 'workshop' ? 'e.g. Steps 1–3 workshop' : 'e.g. Tuesday 7pm Pacific'}
                className={styles.input}
              />
              {fieldErrors.meetingName && <span className={styles.fieldError}>{fieldErrors.meetingName}</span>}

              <label className={styles.label}>Who can see the meeting location?</label>
              <p className={styles.hint}>
                This controls who sees the actual meeting location details (street address, video conferencing link, phone number). What you select here does not affect who can see this home page on Recovery Commons.
              </p>
              <select
                value={linkVisibility}
                onChange={(e) => setLinkVisibility(e.target.value)}
                className={styles.select}
              >
                {LINK_VISIBILITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <label className={styles.label}>Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={kind === 'workshop' ? 'Brief description of the workshop' : 'Brief description of the meeting'}
                className={styles.textarea}
                rows={2}
              />
            </>
          )}

          {step === 3 && (
            <>
              <p className={styles.reviewTitle}>Review</p>
              <dl className={styles.reviewList}>
                <dt>Type</dt>
                <dd>{kind === 'workshop' ? 'Workshop' : 'Meeting'}</dd>
                <dt>Meeting name</dt>
                <dd>{meetingName.trim() || '—'}</dd>
                <dt>Meeting code</dt>
                <dd>{createdMeetingCode || '(assigned when you create)'}</dd>
                {description && (
                  <>
                    <dt>Description</dt>
                    <dd>{description}</dd>
                  </>
                )}
                {kind === 'meeting' && scheduleDay && (
                  <>
                    <dt>Schedule</dt>
                    <dd>
                      {scheduleDay}
                      {scheduleStartTime && ` at ${scheduleStartTime}`}
                      {scheduleDurationMinutes && ` · ${durationLabel(scheduleDurationMinutes, scheduleDurationCustom)}`}
                      {tzOption && ` · ${tzOption.label}`}
                    </dd>
                  </>
                )}
                {kind === 'workshop' && workshopFirstSessionDate && (
                  <>
                    <dt>First session</dt>
                    <dd>
                      {workshopFirstSessionDate}
                      {scheduleStartTime && ` at ${scheduleStartTime}`}
                      {workshopDurationMinutes && ` · ${durationLabel(workshopDurationMinutes, workshopDurationCustom)}`}
                      {tzOption && ` · ${tzOption.label}`}
                    </dd>
                  </>
                )}
                <dt>Format</dt>
                <dd>{formatLabel(format)}</dd>
                {location && (
                  <>
                    <dt>Location</dt>
                    <dd>{location}</dd>
                  </>
                )}
                <dt>Who can see meeting location</dt>
                <dd>{linkVisibilityLabel(linkVisibility)}</dd>
              </dl>
            </>
          )}

          {step === 4 && (
            <>
              <h3 className={styles.successTitle}>
                Your {kind === 'workshop' ? 'workshop' : 'meeting'} home page &lsquo;{meetingName.trim() || 'Meeting'}&rsquo; has been created (meeting code = {createdMeetingCode})
              </h3>
              <p className={styles.successBody}>
                People can find and join it at{' '}
                <strong>
                  {typeof window !== 'undefined' ? `${window.location.origin}/meetings/${createdMeetingCode}` : `/meetings/${createdMeetingCode}`}
                </strong>
                .
              </p>
              <p className={styles.successBody}>
                Add more details on the meeting Settings page: Zoom link, script, readings, service positions, invite members.
              </p>
            </>
          )}

          {error && <p className={styles.error}>{error}</p>}
        </div>
        <div className={styles.footer}>
          {step === 1 ? (
            <>
              <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
              <button type="button" className={styles.primaryBtn} onClick={handleNext}>Next</button>
            </>
          ) : step === 2 ? (
            <>
              <button type="button" className={styles.cancelBtn} onClick={() => { setStep(1); setFieldErrors({}); }}>Back</button>
              <button type="button" className={styles.primaryBtn} onClick={handleNext}>Review</button>
            </>
          ) : step === 3 ? (
            <>
              <button type="button" className={styles.cancelBtn} onClick={() => { setStep(2); setFieldErrors({}); }}>Back</button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleNext}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : `Create ${kind === 'workshop' ? 'Workshop' : 'Meeting'} home page`}
              </button>
            </>
          ) : (
            <>
              <button type="button" className={styles.cancelBtn} onClick={() => onSuccess(createdMeetingCode, false)}>Close</button>
              <button type="button" className={styles.primaryBtn} onClick={() => onSuccess(createdMeetingCode, true)}>
                Go to {kind === 'workshop' ? 'workshop' : 'meeting'} page
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateMeetingModal;
